/** The number of pixels we scroll initially when pressing the button. */
const INITIAL_SCROLL_DISTANCE = 40;

/** The delay when pressing the button after which we begin smoothly scrolling the scrollable. */
const SCROLL_BUTTON_DELAY_MS = 375;

/** The number of pixels we scroll per millisecond while holding down the button. */
const SCROLL_BUTTON_SPEED = 1;

export class ScrollButtonController {
  private direction: 'horizontal' | 'vertical';
  private sign: -1 | 1;
  private scrollable: HTMLDivElement;
  private button: HTMLButtonElement;
  private isPressed = false;

  constructor(options: {
    button: HTMLButtonElement;
    direction: 'horizontal' | 'vertical';
    scrollable: HTMLDivElement;
    sign: -1 | 1;
  }) {
    this.direction = options.direction;
    this.sign = options.sign;
    this.scrollable = options.scrollable;
    this.button = options.button;
  }

  /** Attach event listeners to the button. */
  connect() {
    const onPointerDown = this.onPointerDown.bind(this);
    const onPointerUp = this.onPointerUp.bind(this);

    this.button.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    /**
     * A press can end without a pointerup: a button that becomes disabled while held loses its
     * implicit pointer capture, and touch gestures can be taken over by the browser. Without
     * this the press would never end and the scroll loop would run for the life of the page.
     */
    window.addEventListener('pointercancel', onPointerUp);

    return () => {
      this.button.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      this.endPress();
    };
  }

  private onPointerDown() {
    this.startPress();
  }

  private onPointerUp() {
    this.endPress();
  }

  /**
   * Called when the user begins pressing the button. Matching the behavior of Windows, we initially
   * scroll a fixed distance, and then begin smoothly scrolling if the user continues to hold down
   * the button.
   */
  private startPress() {
    this.isPressed = true;
    this.scrollBy(INITIAL_SCROLL_DISTANCE);

    const startTime = performance.now();
    let previousTime: number | null = null;

    // Needs to be an arrow function to preserve the meaning of this
    const scrollWhilePressed = () => {
      if (!this.isPressed) return;
      const currentTime = performance.now();

      if (currentTime - startTime >= SCROLL_BUTTON_DELAY_MS) {
        const deltaTime = currentTime - (previousTime ?? currentTime);
        if (deltaTime > 0) this.scrollBy(deltaTime * SCROLL_BUTTON_SPEED);
        previousTime = currentTime;
      }

      requestAnimationFrame(scrollWhilePressed);
    };

    scrollWhilePressed();
  }

  private endPress() {
    this.isPressed = false;
  }

  private scrollBy(amount: number) {
    const signedAmount = amount * this.sign;
    this.scrollable.scrollBy(
      this.isHorizontal ? signedAmount : 0,
      this.isHorizontal ? 0 : signedAmount
    );
  }

  private get isHorizontal() {
    return this.direction === 'horizontal';
  }
}
